import * as React from 'react';
import * as RechartsPrimitive from 'recharts';

import { cn } from '../../lib/utils';

// Format: { THEME_NAME: CSS_SELECTOR }
const THEMES = { light: '', dark: '.dark' } as const;

export type ChartConfig = {
    [k in string]: {
        label?: React.ReactNode;
        icon?: React.ComponentType;
    } & ({ color?: string; theme?: never } | { color?: never; theme: Record<keyof typeof THEMES, string> });
};

type ChartContextProps = {
    config: ChartConfig;
};

const ChartContext = React.createContext<ChartContextProps | null>(null);

function useChart() {
    const context = React.useContext(ChartContext);

    if (!context) {
        throw new Error('useChart must be used within a <ChartContainer />');
    }

    return context;
}

function ChartContainer({
    id,
    className,
    children,
    config,
    ...props
}: React.ComponentProps<'div'> & {
    config: ChartConfig;
    children: React.ComponentProps<typeof RechartsPrimitive.ResponsiveContainer>['children'];
}) {
    const uniqueId = React.useId();
    const chartId = `chart-${id || uniqueId.replace(/:/g, '')}`;

    return (
        <ChartContext.Provider value={{ config }}>
            <div
                data-slot='chart'
                data-chart={chartId}
                className={cn(
                    "[&_.recharts-cartesian-axis-tick_text]:fill-muted-foreground [&_.recharts-cartesian-grid_line[stroke='#ccc']]:stroke-border/50 [&_.recharts-curve.recharts-tooltip-cursor]:stroke-border [&_.recharts-polar-grid_[stroke='#ccc']]:stroke-border [&_.recharts-radial-bar-background-sector]:fill-muted [&_.recharts-rectangle.recharts-tooltip-cursor]:fill-muted [&_.recharts-reference-line_[stroke='#ccc']]:stroke-border flex aspect-video justify-center text-xs [&_.recharts-dot[stroke='#fff']]:stroke-transparent [&_.recharts-layer]:outline-hidden [&_.recharts-sector]:outline-hidden [&_.recharts-sector[stroke='#fff']]:stroke-transparent [&_.recharts-surface]:outline-hidden",
                    className
                )}
                {...props}
            >
                <ChartStyle id={chartId} config={config} />
                <RechartsPrimitive.ResponsiveContainer>{children}</RechartsPrimitive.ResponsiveContainer>
            </div>
        </ChartContext.Provider>
    );
}

const ChartStyle = ({ id, config }: { id: string; config: ChartConfig }) => {
    const colorConfig = Object.entries(config).filter(([, config]) => config.theme || config.color);

    if (!colorConfig.length) {
        return null;
    }

    return (
        <style
            dangerouslySetInnerHTML={{
                __html: Object.entries(THEMES)
                    .map(
                        ([theme, prefix]) => `
${prefix} [data-chart=${id}] {
${colorConfig
    .map(([key, itemConfig]) => {
        const color = itemConfig.theme?.[theme as keyof typeof itemConfig.theme] || itemConfig.color;
        return color ? `  --color-${key}: ${color};` : null;
    })
    .join('\n')}
}
`
                    )
                    .join('\n'),
            }}
        />
    );
};

const ChartTooltip = RechartsPrimitive.Tooltip;

function ChartTooltipContent({
    active,
    payload,
    className,
    indicator = 'dot',
    hideLabel = false,
    hideIndicator = false,
    label,
    labelFormatter,
    labelClassName,
    formatter,
    color,
    nameKey,
    labelKey,
}: React.ComponentProps<typeof RechartsPrimitive.Tooltip> &
    React.ComponentProps<'div'> & {
        hideLabel?: boolean;
        hideIndicator?: boolean;
        indicator?: 'line' | 'dot' | 'dashed';
        nameKey?: string;
        labelKey?: string;
    }) {
    const { config } = useChart();

    const tooltipLabel = React.useMemo(() => {
        if (hideLabel || !payload?.length) {
            return null;
        }

        const [item] = payload;
        const key = `${labelKey || item?.dataKey || item?.name || 'value'}`;
        const itemConfig = getPayloadConfigFromPayload(config, item, key);
        const value = !labelKey && typeof label === 'string' ? config[label as keyof typeof config]?.label || label : itemConfig?.label;

        if (labelFormatter) {
            return <div className={cn('font-medium', labelClassName)}>{labelFormatter(value, payload)}</div>;
        }

        if (!value) {
            return null;
        }

        return <div className={cn('font-medium', labelClassName)}>{value}</div>;
    }, [label, labelFormatter, payload, hideLabel, labelClassName, config, labelKey]);

    if (!active || !payload?.length) {
        return null;
    }

    // 날짜 포맷팅 (현재 날짜를 사용하거나 label에서 추출)
    const formatDate = (dateLabel: string | number) => {
        if (typeof dateLabel === 'string') {
            return dateLabel;
        }
        const date = new Date(dateLabel);
        return `${date.getFullYear()}년 ${date.getMonth() + 1}월 ${date.getDate()}일`;
    };

    const currentDate = formatDate(label || new Date());

    // 디버깅을 위한 payload 로그 (개발 환경에서만)
    if (process.env.NODE_ENV === 'development') {
        console.log('Chart Tooltip Payload:', payload);
        console.log('Chart Tooltip Label:', label);
    }

    // 칼로리 데이터 추출
    const foodIntake = payload.find(item => item.dataKey === 'foodCalories');
    const targetIntake = payload.find(item => item.dataKey === 'targetCalories');
    const exerciseExpenditure = payload.find(item => item.dataKey === 'exerciseCalories');
    const targetExercise = payload.find(item => item.dataKey === 'targetExerciseCalories');



    return (
        <div className={cn('bg-white rounded-lg border border-gray-200 shadow-xl p-4 min-w-[160px]', className)}>
            {/* 날짜 헤더 */}
            <div style={{color: 'var(--Color-Text-nomal, #101010)', fontSize: 12, fontWeight: '600'}}>
                {currentDate}
            </div>
            
            {/* 구분선 */}
            <div className="border-t border-gray-300 mb-3"></div>
            
            {/* 음식 섭취 섹션 */}
            <div className="space-y-2 mb-3">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: 'var(--color-foodCalories)' }}></div>
                        <span style={{color: 'var(--Color-Text-weak, #999999)', fontSize: 12, fontWeight: '400'}}>음식 섭취</span>
                    </div>
                    <span style={{color: 'var(--Color-Text-nomal, #101010)', fontSize: 12, fontWeight: '600'}}>
                        {(foodIntake?.value || 0)}kcal
                    </span>
                </div>
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: '#BB8509' }}></div>
                        <span style={{color: 'var(--Color-Text-weak, #999999)', fontSize: 12, fontWeight: '400'}}>목표 섭취</span>
                    </div>
                    <span style={{color: 'var(--Color-Text-nomal, #666666)', fontSize: 12, fontWeight: '500'}}>
                        {(targetIntake?.value || 0)}kcal
                    </span>
                </div>
            </div>

            {/* 디바이더 */}
            <div style={{width: '100%', height: '1px', position: 'relative', background: 'var(--Color-Border-weak, #F4F4F5)', marginBottom: '12px'}} />
            
            {/* 운동 섹션 */}
            <div className="space-y-2">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: 'var(--color-exerciseCalories)' }}></div>
                        <span style={{color: 'var(--Color-Text-weak, #999999)', fontSize: 12, fontWeight: '400'}}>운동 소모</span>
                    </div>
                    <span style={{color: 'var(--Color-Text-nomal, #101010)', fontSize: 12, fontWeight: '600'}}>
                        {(exerciseExpenditure?.value || 0)}kcal
                    </span>
                </div>
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: '#409900' }}></div>
                        <span style={{color: 'var(--Color-Text-weak, #999999)', fontSize: 12, fontWeight: '400'}}>목표 운동</span>
                    </div>
                    <span style={{color: 'var(--Color-Text-nomal, #666666)', fontSize: 12, fontWeight: '500'}}>
                        {(targetExercise?.value || 0)}kcal
                    </span>
                </div>
            </div>
        </div>
    );
}

const ChartLegend = RechartsPrimitive.Legend;

function ChartLegendContent({
    className,
    hideIcon = false,
    payload,
    verticalAlign = 'bottom',
    nameKey,
}: React.ComponentProps<'div'> &
    Pick<RechartsPrimitive.LegendProps, 'payload' | 'verticalAlign'> & {
        hideIcon?: boolean;
        nameKey?: string;
    }) {
    const { config } = useChart();

    if (!payload?.length) {
        return null;
    }

    return (
        <div className={cn('flex items-center justify-center gap-4', verticalAlign === 'top' ? 'pb-3' : 'pt-3', className)}>
            {payload.map((item) => {
                const key = `${nameKey || item.dataKey || 'value'}`;
                const itemConfig = getPayloadConfigFromPayload(config, item, key);

                return (
                    <div key={item.value} className={cn('[&>svg]:text-muted-foreground flex items-center gap-1.5 [&>svg]:h-3 [&>svg]:w-3')}>
                        {itemConfig?.icon && !hideIcon ? (
                            <itemConfig.icon />
                        ) : (
                            <div
                                className='h-2 w-2 shrink-0 rounded-[2px]'
                                style={{
                                    backgroundColor: item.color,
                                }}
                            />
                        )}
                        {itemConfig?.label}
                    </div>
                );
            })}
        </div>
    );
}

// Helper to extract item config from a payload.
function getPayloadConfigFromPayload(config: ChartConfig, payload: unknown, key: string) {
    if (typeof payload !== 'object' || payload === null) {
        return undefined;
    }

    const payloadPayload = 'payload' in payload && typeof payload.payload === 'object' && payload.payload !== null ? payload.payload : undefined;

    let configLabelKey: string = key;

    if (key in payload && typeof payload[key as keyof typeof payload] === 'string') {
        configLabelKey = payload[key as keyof typeof payload] as string;
    } else if (payloadPayload && key in payloadPayload && typeof payloadPayload[key as keyof typeof payloadPayload] === 'string') {
        configLabelKey = payloadPayload[key as keyof typeof payloadPayload] as string;
    }

    return configLabelKey in config ? config[configLabelKey] : config[key as keyof typeof config];
}

export { ChartContainer, ChartTooltip, ChartTooltipContent, ChartLegend, ChartLegendContent, ChartStyle };
